import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2, ArrowUpAZ, ArrowDownAZ, Download, Copy, RefreshCw } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { sortRecords } from "../../lib/sortRecords";
import { TenantTable, TENANT_MODULES as MODULES } from "../../components/admin/TenantTable";
import { TenantForm } from "../../components/admin/TenantForm";
import { PageHeader, StatCard, FormDialog, SelectInput, ActionButton } from "../../components/common";

const EMPTY = { name: "", admin_name: "", admin_email: "", admin_password: "", plan: "starter", enabled_modules: ["swd"], manager_email: "" };

const SORT_FIELDS = [
  { value: "name", label: "Company" },
  { value: "id", label: "Tenant ID" },
  { value: "plan", label: "Plan" },
  { value: "status", label: "Status" },
  { value: "user_count", label: "Users" },
  { value: "customer_count", label: "Customers" },
  { value: "created_at", label: "Created" },
];
const NUMERIC_SORT = ["user_count", "customer_count"];

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [overview, setOverview] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, o] = await Promise.all([api.get("/admin/tenants"), api.get("/admin/overview")]);
      setTenants(t.data);
      setOverview(o.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleModule = async (tenant, moduleKey, enabled) => {
    const modules = enabled
      ? [...new Set([...(tenant.enabled_modules || []), moduleKey])]
      : (tenant.enabled_modules || []).filter((m) => m !== moduleKey);
    try {
      const r = await api.patch(`/admin/tenants/${tenant.id}`, { enabled_modules: modules });
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? r.data : t)));
      toast.success(`${tenant.name}: ${moduleKey} ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const updateField = async (tenant, patch) => {
    try {
      const r = await api.patch(`/admin/tenants/${tenant.id}`, patch);
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? r.data : t)));
      toast.success(`${tenant.name} updated`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteTenant = async (t) => {
    if (!window.confirm(`Delete tenant "${t.name}" and ALL of its data (users, customers, assets, locations, tickets, orders)? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/admin/tenants/${t.id}`);
      const removed = Object.entries(r.data.removed || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Tenant ${t.name} deleted${removed ? ` — removed ${removed}` : ""}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sortedTenants = useMemo(() => sortRecords(tenants, sortBy, sortDir, NUMERIC_SORT), [tenants, sortBy, sortDir]);

  const createTenant = async (e) => {    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, manager_email: form.manager_email || undefined };
      await api.post("/admin/tenants", payload);
      toast.success(`Tenant "${form.name}" onboarded`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const [keyFor, setKeyFor] = useState(null);
  const [keyData, setKeyData] = useState(null);
  const [keyBusy, setKeyBusy] = useState(false);

  const viewKey = async (t) => {
    setKeyFor(t);
    setKeyData(null);
    setKeyBusy(true);
    try {
      const r = await api.get(`/admin/tenants/${t.id}/api-key`);
      setKeyData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const regenKey = async () => {
    setKeyBusy(true);
    try {
      const r = await api.post(`/admin/tenants/${keyFor.id}/api-key/regenerate`);
      setKeyData(r.data);
      toast.success("API key regenerated — the old key is revoked");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(keyData?.api_key || "");
    toast.success("API key copied");
  };

  const downloadBackup = async () => {
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wellfleetlogic-backup-${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Database backup downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="tenants-page">
      <PageHeader title="Tenants & Module Billing" subtitle="Onboard customers and charge them for exactly the modules they use" testId="tenants-header">
        <ActionButton testId="download-backup-button" variant="secondary" onClick={downloadBackup}>
          <Download size={16} /> Backup DB
        </ActionButton>
        <ActionButton testId="create-tenant-button" onClick={() => setOpen(true)}>
          <Plus size={16} /> Onboard Tenant
        </ActionButton>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <StatCard label="Tenants" value={overview?.tenant_count ?? "—"} icon={Building2} testId="stat-tenant-count" />
        <StatCard label="Active" value={overview?.active_tenants ?? "—"} icon={Building2} accent="text-emerald-400" testId="stat-active-tenants" />
        <StatCard label="Total Users" value={overview?.user_count ?? "—"} icon={Building2} accent="text-blue-400" testId="stat-total-users" />
        <div className="bg-card border border-slate-700/60 rounded-lg p-5" data-testid="stat-module-adoption">
          <span className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">Module Adoption</span>
          <div className="mt-3 space-y-1">
            {MODULES.map((m) => (
              <div key={m.key} className="flex justify-between text-xs">
                <span className="text-slate-400">{m.label}</span>
                <span className="font-mono text-amber-400">{overview?.module_adoption?.[m.key] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4" data-testid="tenant-sort-controls">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">Sort by</span>
        <SelectInput
          testId="tenant-sort-field"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          options={SORT_FIELDS}
          className="!w-44 !bg-white !text-slate-100"
        />
        <button
          data-testid="tenant-sort-direction"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors duration-200"
        >
          {sortDir === "asc" ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />}
          {sortDir === "asc" ? "A → Z" : "Z → A"}
        </button>
      </div>

      <TenantTable tenants={sortedTenants} {...{ updateField, toggleModule, viewKey, deleteTenant }} />

      <FormDialog open={!!keyFor} onOpenChange={(o) => { if (!o) { setKeyFor(null); setKeyData(null); } }} title={`API Key — ${keyFor?.name || ""}`} testId="tenant-apikey-dialog">
        <div className="space-y-4">
          {keyBusy && !keyData && <p className="text-sm text-slate-400">Loading key…</p>}
          {keyData && (
            <>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2.5 text-sm font-mono text-slate-100 break-all" data-testid="tenant-apikey-value">
                  {keyData.api_key}
                </code>
                <ActionButton testId="tenant-apikey-copy" variant="secondary" onClick={copyKey} className="!px-3">
                  <Copy size={14} />
                </ActionButton>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 text-xs text-slate-400 space-y-1.5 font-mono" data-testid="tenant-apikey-instructions">
                <p className="text-slate-300 font-sans font-medium text-sm">Using in Postman</p>
                <p>Base URL: {process.env.REACT_APP_BACKEND_URL}/api</p>
                <p>Header: <span className="text-amber-500">X-API-Key: {keyData.api_key}</span></p>
                <p>Full read + write access as this tenant. Example: GET /customers</p>
              </div>
              <ActionButton testId="tenant-apikey-regen" variant="secondary" onClick={regenKey} disabled={keyBusy} className="w-full justify-center">
                <RefreshCw size={14} /> {keyBusy ? "Regenerating…" : "Regenerate Key (revokes old)"}
              </ActionButton>
            </>
          )}
        </div>
      </FormDialog>

      <TenantForm {...{ open, setOpen, createTenant, form, setForm, busy }} />
    </div>
  );
}
